import { useEffect, useRef } from 'react';

import './styles/modal.css';

// A modal confirmation, on the native <dialog>.
//
// NATIVE ON PURPOSE. `showModal()` gives, without a line of ours: the backdrop,
// the rest of the page made inert (no tabbing out, no clicking behind), Esc to
// close, and focus returned to what opened it. Each of those is a place a
// hand-rolled modal gets subtly wrong.
//
// WHILE `busy`, IT DOES NOT CLOSE — not with Esc, not with the backdrop. A
// confirmation that vanishes mid-request leaves somebody not knowing whether
// what they confirmed happened.
//
// NO TOASTER OF ITS OWN. The dialog sits in the browser's top layer; the app's
// toaster climbs back above it each time one opens (common/components/
// app-toaster). A second <Toaster> here is what used to freeze the app's one,
// and leave «Tarea autorizada» on screen for good.
//
// Short by design: a title, what is being confirmed, and the two buttons. A
// modal that needs scrolling is a page pretending to be a question.

export default function Modal({ open, title, onClose, busy = false, actions, children }) {
    const ref = useRef(null);

    useEffect(() => {
        const dialog = ref.current;
        if (!dialog) return;
        if (open && !dialog.open) dialog.showModal();
        if (!open && dialog.open) dialog.close();
    }, [open]);

    // Esc fires `cancel` before closing: refused while busy, otherwise routed
    // through `onClose` so the parent's state stays the source of truth.
    const handleCancel = (event) => {
        event.preventDefault();
        if (!busy) onClose?.();
    };

    // A click whose target is the <dialog> itself landed on the backdrop: the
    // content is wrapped in a box that fills the dialog's visible area.
    const handleClick = (event) => {
        if (event.target === ref.current && !busy) onClose?.();
    };

    return (
        <dialog
            ref={ref}
            className="modal"
            aria-labelledby="modal-title"
            onCancel={handleCancel}
            onClick={handleClick}
        >
            {open && (
                <div className="modal__box">
                    <h3 className="modal__title" id="modal-title">{title}</h3>
                    <div className="modal__body">{children}</div>
                    {actions && <div className="modal__actions">{actions}</div>}
                </div>
            )}
        </dialog>
    );
}
